import { useMemo } from 'react';
import type {
  AmplitudeEventDynamic,
  GAEventDynamic,
  GAEvent,
  TrackingCallback,
  AmplitudePropertyValues,
  AmplitudeUser,
} from '../types';
import { mergeDeep } from '../utils/mergeDeep';
import { evaluateDynamicValues } from '../utils/evaluateDynamicValues';
import { useTrackingContext } from '../context';

export type TrackingValues<
  T extends AmplitudePropertyValues = AmplitudePropertyValues,
> = {
  amplitude?: AmplitudeEventDynamic<T>;
  ga?: GAEventDynamic;
  userProperties?: AmplitudeUser;
};

export const useTracking = <
  T extends AmplitudePropertyValues = AmplitudePropertyValues,
>(
  ...values: TrackingValues<T>[]
) => {
  const { trackAll } = useTrackingContext();
  const { events, content } = values.reduce(
    (result, { amplitude, ga, userProperties }) => {
      result.events.push(
        (
          amplitudeEventProperties = {},
          overrideGA = {},
          amplitudeUserProperties = {},
        ) => {
          if (!ga && !amplitude && !userProperties) {
            return;
          }
          const mergedAmplitude = amplitude
            ? {
                label: amplitude.label,
                properties: evaluateDynamicValues(
                  mergeDeep(
                    {},
                    amplitude?.properties || {},
                    amplitudeEventProperties || {},
                  ),
                ),
              }
            : null;

          const mergedGA = ga
            ? (evaluateDynamicValues(
                mergeDeep({}, ga, overrideGA || {}),
              ) as GAEvent)
            : null;

          const mergedUserProperties = userProperties
            ? (mergeDeep(
                {},
                userProperties,
                amplitudeUserProperties || {},
              ) as AmplitudeUser)
            : null;

          trackAll(mergedAmplitude, mergedGA, mergedUserProperties);
        },
      );
      result.content = `${result.content}|${amplitude?.label}|${ga?.label}|${ga?.category}|${ga?.action}`;
      return result;
    },
    { events: [], content: '' } as {
      events: TrackingCallback<T>[];
      content: string;
    },
  );
  return useMemo(() => events, [trackAll, content]);
};

export default useTracking;
